// ============================================================================
// CECODEV Platform - SondageForm Component
// ============================================================================
// Formulaire de création / édition d'un sondage.
// Ouvert en Dialog depuis SondagesList.
// Le pilote coche ce qu'il veut sonder : Intérêt, Préférences demi-journées, Dispo dates précises.
// ============================================================================

import { useState, useEffect } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Switch,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  } from "@mui/material";
import {
  Plus,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, messageApi } from "../lib/api";
import EmailComposer, { type EmailComposerResult } from "./EmailComposer";
import { useIsMobile } from "../hooks/useIsMobile";
import { DialogMobile } from "./ui/DialogMobile";
import ChampDate from "./ChampDate";

// ============================================================================
// Types
// ============================================================================

interface Creneau {
  id?: string;
  dateCreneau: string;
  heureDebut: string;
  heureFin: string;
  lieu: string;
  notes: string;
}

interface Props {
  open: boolean;
  onClose: (saved?: boolean) => void;
  projetId: string;
  groupeId?: string;
  editingData?: any; // SondageDetail from API
  /** Sprint 6 — Si true : on est sur un groupe Inter. Le volet "intérêt"
   *  est masqué (les inscrits sont déjà intéressés par définition). */
  forceInterMode?: boolean;
}

interface GroupeOption {
  id: string;
  nom: string;
}

interface ProduitOption {
  id: string;
  produitNom: string;
}

// ============================================================================
// Helpers — Demi-journées préférences hebdomadaires
// ============================================================================
// On utilise des dates sentinelles (semaine du 3 janvier 2000) pour encoder
// les jours de la semaine dans le champ dateCreneau existant.
// Cela permet de sonder les préférences de demi-journées sans générer de
// dates réelles (les séances sont ensuite planifiées librement).
// ============================================================================

const JOURS_SEMAINE = [
  { value: 1, label: "Lun" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Mer" },
  { value: 4, label: "Jeu" },
  { value: 5, label: "Ven" },
];

/** Dates sentinelles : jour de semaine → date fictive (semaine du 3 janv. 2000) */
export const PREF_DATES: Record<number, string> = {
  1: "2000-01-03", // Lundi
  2: "2000-01-04", // Mardi
  3: "2000-01-05", // Mercredi
  4: "2000-01-06", // Jeudi
  5: "2000-01-07", // Vendredi
};

const JOURS_LABELS: Record<string, string> = {
  "2000-01-03": "Lundi",
  "2000-01-04": "Mardi",
  "2000-01-05": "Mercredi",
  "2000-01-06": "Jeudi",
  "2000-01-07": "Vendredi",
};

/** Détecte si des créneaux utilisent le mode préférence (dates sentinelles) */
function isPreferenceMode(creneaux: { dateCreneau: string }[]): boolean {
  const sentinelDates = Object.values(PREF_DATES);
  return creneaux.length > 0 && creneaux.every(c => sentinelDates.includes(c.dateCreneau));
}

/** Sépare les créneaux en préférences (sentinelles) et dates précises */
export function splitCreneaux<T extends { dateCreneau: string }>(creneaux: T[]): { preferences: T[]; datesPrecises: T[] } {
  const sentinelDates = Object.values(PREF_DATES);
  return {
    preferences: creneaux.filter(c => sentinelDates.includes(c.dateCreneau)),
    datesPrecises: creneaux.filter(c => !sentinelDates.includes(c.dateCreneau)),
  };
}

/** Label lisible pour un créneau en mode préférence. Décode MATIN/APRES-MIDI/SOIR
 *  à partir de l'heureDebut — le créneau du soir (ajouté au Sprint 6) commence
 *  à 18:00, l'après-midi à 14:00 et le matin avant 13:00. */
export function getPreferenceLabel(dateCreneau: string, heureDebut: string): string {
  const jour = JOURS_LABELS[dateCreneau] || dateCreneau;
  const moment =
    heureDebut >= "18:00"
      ? "soir"
      : heureDebut >= "13:00"
        ? "après-midi"
        : "matin";
  return `${jour} ${moment}`;
}

/** Toutes les demi-journées possibles (clés "1-M", "1-A", "1-S", ..., "5-S") */
const ALL_DEMIS_JOUR = JOURS_SEMAINE.flatMap(j => [`${j.value}-M`, `${j.value}-A`]);
const ALL_SOIRS = JOURS_SEMAINE.map(j => `${j.value}-S`);
const ALL_DEMIS = [...ALL_DEMIS_JOUR, ...ALL_SOIRS];

/** Génère les créneaux de préférence à partir des demi-journées actives.
 *  MATIN : 09:00–12:30 · APRES_MIDI : 14:00–17:30 · SOIR : 18:00–21:00 */
function generateCreneauxFromDemis(demisActives: string[]): Creneau[] {
  const creneaux: Creneau[] = [];

  for (const jour of JOURS_SEMAINE) {
    const dateStr = PREF_DATES[jour.value];
    if (!dateStr) continue;

    if (demisActives.includes(`${jour.value}-M`)) {
      creneaux.push({ dateCreneau: dateStr, heureDebut: "09:00", heureFin: "12:30", lieu: "", notes: "" });
    }
    if (demisActives.includes(`${jour.value}-A`)) {
      creneaux.push({ dateCreneau: dateStr, heureDebut: "14:00", heureFin: "17:30", lieu: "", notes: "" });
    }
    if (demisActives.includes(`${jour.value}-S`)) {
      creneaux.push({ dateCreneau: dateStr, heureDebut: "18:00", heureFin: "21:00", lieu: "", notes: "" });
    }
  }

  return creneaux;
}

// ============================================================================
// Component
// ============================================================================

export default function SondageForm({ open, onClose, projetId, groupeId, editingData, forceInterMode }: Props) {
  const isMobile = useIsMobile();
  const isEdit = Boolean(editingData);

  // Form state
  const [titre, setTitre] = useState("");
  const [description, setDescription] = useState("");
  const [selectedGroupeId, setSelectedGroupeId] = useState(groupeId || "");

  // Checkboxes: what to include in this sondage
  const [sonderInteret, setSonderInteret] = useState(false);
  const [sonderDispoStep1, setSonderDispoStep1] = useState(true);
  const [sonderDispoStep2, setSonderDispoStep2] = useState(false);

  const [choixMultiple, setChoixMultiple] = useState(true);
  const [anonyme, setAnonyme] = useState(false);
  const [inclureAnimateurs, setInclureAnimateurs] = useState(true);
  const [inclureStagiaires, setInclureStagiaires] = useState(true);
  const [dateLimiteReponse, setDateLimiteReponse] = useState("");
  const [rappelJ7Actif, setRappelJ7Actif] = useState(true);
  const [rappelJ3Actif, setRappelJ3Actif] = useState(true);

  // Créneaux step1 (préférences demi-journées)
  const [creneauxStep1, setCreneauxStep1] = useState<Creneau[]>([]);
  // Créneaux step2 (manual)
  const [creneauxStep2, setCreneauxStep2] = useState<Creneau[]>([]);

  // Demi-journées actives (clés "1-M", "1-A", "1-S", ..., "5-S").
  // Par défaut seules les matinées et après-midis sont proposées — le
  // créneau du soir est opt-in (Sprint 6).
  const [demisActives, setDemisActives] = useState<string[]>([...ALL_DEMIS_JOUR]);
  const [inclureSoir, setInclureSoir] = useState(false);

  // Groupes & Produits lists
  const [groupesList, setGroupesList] = useState<GroupeOption[]>([]);
  const [produitsList, setProduitsList] = useState<ProduitOption[]>([]);
  const [selectedProduitId, setSelectedProduitId] = useState("");

  const [saving, setSaving] = useState(false);

  // EmailComposer
  const [composerOpen, setComposerOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  // Nombre de destinataires estimé
  const [nbDestinatairesEstime, setNbDestinatairesEstime] = useState(0);

  // Compute: at least one checkbox must be checked
  const hasSelection = sonderInteret || sonderDispoStep1 || sonderDispoStep2;

  // ============================================================================
  // Init
  // ============================================================================

  useEffect(() => {
    if (!open) return;

    // Load groupes for selector (only if groupeId not fixed)
    if (!groupeId) {
      api.get("/groupes", { params: { projetId } })
        .then((res) => setGroupesList(res.data.groupes || []))
        .catch(() => {});
    }

    // Load produits for selector
    api.get(`/projets/${projetId}`)
      .then((res) => {
        const produits = res.data.produits || [];
        setProduitsList(produits);
        if (produits.length === 1) setSelectedProduitId(produits[0].id);
      })
      .catch(() => {});

    if (editingData) {
      const s = editingData.sondage;
      setTitre(s.titre || "");
      setDescription(s.description || "");
      setSelectedGroupeId(s.groupeId || "");
      setSelectedProduitId(s.projetProduitId || "");
      setSonderInteret(s.sonderInteret || false);
      setChoixMultiple(s.choixMultiple ?? true);
      setAnonyme(s.anonyme || false);
      setInclureAnimateurs(s.inclureAnimateurs ?? true);
      setInclureStagiaires(s.inclureStagiaires ?? true);
      setDateLimiteReponse(s.dateLimiteReponse ? s.dateLimiteReponse.slice(0, 10) : "");
      setRappelJ7Actif(s.rappelJ7Actif ?? true);
      setRappelJ3Actif(s.rappelJ3Actif ?? true);

      // Infer checkbox state from existing creneaux
      const existingCreneaux = editingData.creneaux || [];
      if (existingCreneaux.length > 0) {
        if (isPreferenceMode(existingCreneaux)) {
          // Mode préférences demi-journées
          setSonderDispoStep1(true);
          setSonderDispoStep2(false);
          // Retrouver les demi-journées actives depuis les créneaux existants
          const dateToDayMap: Record<string, number> = {};
          for (const [day, date] of Object.entries(PREF_DATES)) {
            dateToDayMap[date] = Number(day);
          }
          const activeDemis: string[] = [];
          for (const c of existingCreneaux) {
            const day = dateToDayMap[c.dateCreneau];
            if (!day) continue;
            const period = c.heureDebut < "13:00" ? "M" : "A";
            activeDemis.push(`${day}-${period}`);
          }
          setDemisActives(activeDemis);
          setCreneauxStep2([]);
        } else {
          // Mode dates précises
          setSonderDispoStep1(false);
          setSonderDispoStep2(true);
          setCreneauxStep2((existingCreneaux as Array<{ dateCreneau: string; heureDebut: string; heureFin: string; lieu?: string | null; notes?: string | null }>).map((c) => ({
            dateCreneau: c.dateCreneau,
            heureDebut: c.heureDebut,
            heureFin: c.heureFin,
            lieu: c.lieu || "",
            notes: c.notes || "",
          })));
          setCreneauxStep1([]);
        }
      } else {
        setSonderDispoStep1(false);
        setSonderDispoStep2(false);
        setCreneauxStep1([]);
        setCreneauxStep2([]);
      }
    } else {
      // Reset form
      setTitre("");
      setDescription("");
      setSelectedGroupeId(groupeId || "");
      setSelectedProduitId("");
      setSonderInteret(false);
      setSonderDispoStep1(true);
      setSonderDispoStep2(false);
      setChoixMultiple(true);
      setAnonyme(false);
      setInclureAnimateurs(true);
      setInclureStagiaires(true);
      setDateLimiteReponse("");
      setRappelJ7Actif(true);
      setRappelJ3Actif(true);
      setCreneauxStep1([]);
      setCreneauxStep2([]);
      setDemisActives([...ALL_DEMIS]);
    }
  }, [open, editingData, projetId, groupeId]);

  // Estimer le nombre de destinataires en fonction du groupe sélectionné
  useEffect(() => {
    if (!open) return;

    const targetGroupeId = selectedGroupeId || groupeId;

    if (targetGroupeId) {
      api.get(`/groupes/${targetGroupeId}`)
        .then((res) => {
          const participants = res.data.participants || [];
          const animateur = res.data.groupe?.animateurId ? 1 : 0;
          let n = 0;
          if (inclureStagiaires) n += participants.length;
          if (inclureAnimateurs) n += animateur;
          setNbDestinatairesEstime(n);
        })
        .catch(() => setNbDestinatairesEstime(0));
    } else {
      api.get(`/projets/${projetId}`)
        .then((res) => {
          const data = res.data;
          let n = 0;
          if (inclureStagiaires) n += data.participants?.length || 0;
          if (inclureAnimateurs) n += data.animateurs?.length || 0;
          setNbDestinatairesEstime(n);
        })
        .catch(() => setNbDestinatairesEstime(0));
    }
  }, [open, selectedGroupeId, groupeId, projetId, inclureAnimateurs, inclureStagiaires]);

  // ============================================================================
  // Auto-génération des créneaux demi-journées quand les jours changent
  // ============================================================================

  useEffect(() => {
    if (!sonderDispoStep1) return;
    if (demisActives.length === 0) {
      setCreneauxStep1([]);
      return;
    }
    setCreneauxStep1(generateCreneauxFromDemis(demisActives));
  }, [demisActives, sonderDispoStep1]);

  // ============================================================================
  // Save
  // ============================================================================

  const handleSave = async (envoyerImmediatement = false) => {
    if (!hasSelection) {
      toast.error("Cochez au moins une option (intérêt ou disponibilité)");
      return;
    }

    // Combine creneaux
    const allCreneaux = [...creneauxStep1, ...creneauxStep2].filter(
      (c) => c.dateCreneau && c.dateCreneau.trim() !== ""
    );

    if ((sonderDispoStep1 || sonderDispoStep2) && allCreneaux.length === 0) {
      toast.error("Sélectionnez au moins un jour ou ajoutez des créneaux");
      return;
    }

    if (!inclureAnimateurs && !inclureStagiaires) {
      toast.error("Sélectionnez au moins un type de destinataire");
      return;
    }

    const payload: any = {
      projetId,
      groupeId: selectedGroupeId || null,
      projetProduitId: selectedProduitId || null,
      titre: titre || undefined,
      description: description || undefined,
      sonderInteret,
      sonderAttentes: false,
      choixMultiple,
      anonyme,
      inclureAnimateurs,
      inclureStagiaires,
      dateLimiteReponse: dateLimiteReponse || undefined,
      rappelJ7Actif,
      rappelJ3Actif,
      creneaux: allCreneaux,
      envoyerImmediatement,
    };

    if (envoyerImmediatement && !isEdit) {
      setPendingPayload(payload);
      setComposerOpen(true);
      return;
    }

    await doSave(payload);
  };

  const doSave = async (payload: any) => {
    try {
      setSaving(true);

      if (isEdit) {
        await api.patch(`/sondages/${editingData.sondage.id}`, payload);
        toast.success("Sondage mis à jour");
      } else {
        const response = await api.post("/sondages", payload);
        const data = response.data;

        if (!payload.envoyerImmediatement) {
          toast.success("Sondage créé en brouillon");
        } else if (data.noApiKey) {
          toast.warning(
            `Sondage créé mais les emails n'ont pas été envoyés : clé Resend non configurée. Configurez RESEND_API_KEY dans .env.local`,
            { duration: 8000 }
          );
        } else if (data.nbEmailsErreurs > 0 && data.nbEmailsEnvoyes === 0) {
          toast.error(
            `Sondage créé mais aucun email n'a pu être envoyé (${data.nbEmailsErreurs} erreur(s))`,
            { duration: 6000 }
          );
        } else if (data.nbEmailsErreurs > 0) {
          toast.warning(
            `Sondage créé — ${data.nbEmailsEnvoyes} email(s) envoyé(s), ${data.nbEmailsErreurs} erreur(s)`,
            { duration: 6000 }
          );
        } else {
          toast.success(
            `Sondage créé et envoyé à ${data.nbDestinataires} destinataire(s)`
          );
        }
      }

      onClose(true);
    } catch (error) {
      toast.error(messageApi(error, "Erreur lors de la sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  const handleComposerConfirm = (config: EmailComposerResult) => {
    setComposerOpen(false);

    if (!pendingPayload) return;

    const isImmediat = !config.dateEnvoi;

    const enrichedPayload = {
      ...pendingPayload,
      envoyerImmediatement: isImmediat,
      emailSujet: config.sujet,
      emailCorps: config.corpsHtml,
      emailCompteId: config.compteEmailId,
      emailCc: config.cc.length > 0 ? config.cc : undefined,
      emailAttachments: config.attachments.length > 0 ? config.attachments : undefined,
      dateEnvoi: config.dateEnvoi,
    };

    doSave(enrichedPayload);
  };

  const getDefaultSondageEmail = () => {
    const sondageTitre = titre || "Sondage de disponibilités";
    return {
      sujet: `Sondage : ${sondageTitre}`,
      corps: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;">
  <h2 style="color:#4BB7A5;">Nouveau sondage</h2>
  <p>Bonjour {{prenom}},</p>
  <p>Vous êtes invité(e) à répondre au sondage <strong>${sondageTitre}</strong>.</p>
  ${description ? `<p>${description}</p>` : ""}
  <p><a href="{{lien_sondage}}" style="display:inline-block;padding:12px 24px;background:#4BB7A5;color:white;text-decoration:none;border-radius:4px;margin:16px 0;">Répondre au sondage</a></p>
  <p style="color:#666;font-size:12px;">Ce lien est personnel.</p>
</div>`,
    };
  };

  // ============================================================================
  // Creneaux step2 management
  // ============================================================================

  const addCreneau = () => {
    setCreneauxStep2([...creneauxStep2, { dateCreneau: "", heureDebut: "09:00", heureFin: "12:30", lieu: "", notes: "" }]);
  };

  const removeCreneau = (index: number) => {
    setCreneauxStep2(creneauxStep2.filter((_, i) => i !== index));
  };

  const updateCreneau = (index: number, field: keyof Creneau, value: string) => {
    const updated = [...creneauxStep2];
    updated[index] = { ...updated[index], [field]: value };
    setCreneauxStep2(updated);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <DialogMobile open={open} onClose={() => onClose()} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isEdit ? "Modifier le sondage" : "Nouveau sondage"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          {/* Que souhaitez-vous sonder ? */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Que souhaitez-vous sonder ?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* Sprint 6 — Le sondage d'intérêt ne s'applique pas aux groupes
                  Inter (les participants se sont inscrits d'eux-mêmes). */}
              {!forceInterMode && (
                <FormControlLabel
                  control={<Checkbox checked={sonderInteret} onChange={(e) => setSonderInteret(e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Intérêt</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Les participants indiquent s'ils sont intéressés (oui / non)
                      </Typography>
                    </Box>
                  }
                />
              )}
              <FormControlLabel
                control={<Checkbox checked={sonderDispoStep1} onChange={(e) => setSonderDispoStep1(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Préférences demi-journées</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Les participants indiquent leurs demi-journées préférées ou moins favorables dans la semaine
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={sonderDispoStep2} onChange={(e) => setSonderDispoStep2(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Disponibilité dates précises</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Dates et horaires spécifiques définis manuellement
                    </Typography>
                  </Box>
                }
              />
            </Box>
            {!hasSelection && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Cochez au moins une option.
              </Alert>
            )}
          </Box>

          <Divider />

          {/* Infos générales */}
          <TextField
            label="Titre du sondage"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            fullWidth
            placeholder="Titre auto-généré si vide"
          />

          <TextField
            label="Description (optionnel)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          {/* Produit selector */}
          {produitsList.length > 1 && (
            <FormControl fullWidth>
              <InputLabel>Produit</InputLabel>
              <Select
                value={selectedProduitId}
                onChange={(e) => setSelectedProduitId(e.target.value)}
                label="Produit"
              >
                <MenuItem value="">Non rattaché</MenuItem>
                {produitsList.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.produitNom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Groupe selector (if not fixed) */}
          {!groupeId && groupesList.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Groupe (optionnel)</InputLabel>
              <Select
                value={selectedGroupeId}
                onChange={(e) => setSelectedGroupeId(e.target.value)}
                label="Groupe (optionnel)"
              >
                <MenuItem value="">Tous les groupes du projet</MenuItem>
                {groupesList.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date limite de réponse (toujours visible si au moins une option cochée) */}
          {hasSelection && (
            <ChampDate fullWidth={false}
              label="Date limite de réponse"
              value={dateLimiteReponse}
              onChange={(v) => setDateLimiteReponse(v)}
            />
          )}

          {/* Destinataires */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Destinataires
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={inclureStagiaires} onChange={(e) => setInclureStagiaires(e.target.checked)} />}
              label="Participants (stagiaires inscrits)"
            />
            <FormControlLabel
              control={<Checkbox checked={inclureAnimateurs} onChange={(e) => setInclureAnimateurs(e.target.checked)} />}
              label="Animateurs"
            />
          </Box>

          <Divider />

          {/* Préférences demi-journées — step 1 */}
          {sonderDispoStep1 && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Préférences demi-journées
                </Typography>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => {
                    const universe = inclureSoir ? ALL_DEMIS : ALL_DEMIS_JOUR;
                    setDemisActives(demisActives.length === universe.length ? [] : [...universe]);
                  }}
                >
                  {demisActives.length === (inclureSoir ? ALL_DEMIS.length : ALL_DEMIS_JOUR.length)
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Cliquez pour activer ou désactiver chaque demi-journée. (
                {demisActives.length}/{inclureSoir ? ALL_DEMIS.length : ALL_DEMIS_JOUR.length} sélectionnées)
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "60px repeat(5, 1fr)", sm: "85px repeat(5, 1fr)" }, gap: { xs: 0.5, sm: 0.75 }, alignItems: "center" }}>
                {/* En-têtes jours */}
                <Box />
                {JOURS_SEMAINE.map((j) => (
                  <Typography key={j.value} variant="caption" sx={{ textAlign: "center", fontWeight: 600 }}>
                    {j.label}
                  </Typography>
                ))}

                {/* Ligne Matin */}
                <Typography variant="caption" color="text.secondary">Matin</Typography>
                {JOURS_SEMAINE.map((j) => {
                  const key = `${j.value}-M`;
                  const active = demisActives.includes(key);
                  return (
                    <Box
                      key={key}
                      onClick={() => setDemisActives(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}
                      sx={{
                        py: 0.75,
                        borderRadius: 1,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: active ? "primary.main" : "grey.50",
                        color: active ? "primary.contrastText" : "text.disabled",
                        border: "1px solid",
                        borderColor: active ? "primary.main" : "grey.300",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        userSelect: "none",
                        transition: "all 0.15s",
                        "&:hover": { opacity: 0.8 },
                      }}
                    >
                      {j.label}
                    </Box>
                  );
                })}

                {/* Ligne Après-midi */}
                <Typography variant="caption" color="text.secondary">Après-midi</Typography>
                {JOURS_SEMAINE.map((j) => {
                  const key = `${j.value}-A`;
                  const active = demisActives.includes(key);
                  return (
                    <Box
                      key={key}
                      onClick={() => setDemisActives(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}
                      sx={{
                        py: 0.75,
                        borderRadius: 1,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: active ? "primary.main" : "grey.50",
                        color: active ? "primary.contrastText" : "text.disabled",
                        border: "1px solid",
                        borderColor: active ? "primary.main" : "grey.300",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        userSelect: "none",
                        transition: "all 0.15s",
                        "&:hover": { opacity: 0.8 },
                      }}
                    >
                      {j.label}
                    </Box>
                  );
                })}

                {/* Ligne Soir — visible seulement si l'option est cochée (Sprint 6) */}
                {inclureSoir && (
                  <>
                    <Typography variant="caption" color="text.secondary">Soir (18h-21h)</Typography>
                    {JOURS_SEMAINE.map((j) => {
                      const key = `${j.value}-S`;
                      const active = demisActives.includes(key);
                      return (
                        <Box
                          key={key}
                          onClick={() => setDemisActives(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])}
                          sx={{
                            py: 0.75,
                            borderRadius: 1,
                            textAlign: "center",
                            cursor: "pointer",
                            bgcolor: active ? "primary.main" : "grey.50",
                            color: active ? "primary.contrastText" : "text.disabled",
                            border: "1px solid",
                            borderColor: active ? "primary.main" : "grey.300",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            userSelect: "none",
                            transition: "all 0.15s",
                            "&:hover": { opacity: 0.8 },
                          }}
                        >
                          {j.label}
                        </Box>
                      );
                    })}
                  </>
                )}
              </Box>

              {/* Option "inclure le créneau du soir" (Sprint 6). Décocher
                  retire automatiquement les keys SOIR de la sélection. */}
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={inclureSoir}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setInclureSoir(checked);
                      if (!checked) {
                        setDemisActives((prev) => prev.filter((k) => !k.endsWith("-S")));
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Inclure aussi le créneau du soir (18h-21h)
                  </Typography>
                }
              />

              {demisActives.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Sélectionnez au moins une demi-journée.
                </Alert>
              )}
            </Box>
          )}

          {/* Créneaux — dispo step 2 (manual) */}
          {sonderDispoStep2 && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Dates précises — Créneaux proposés
                </Typography>
                <Chip label={creneauxStep2.length} size="small" />
              </Box>

              {creneauxStep2.map((c, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "150px 110px 110px 1fr auto" },
                    gap: 1,
                    mb: 1,
                    alignItems: "center",
                  }}
                >
                  <ChampDate fullWidth={false}
                    size="small"
                    value={c.dateCreneau}
                    onChange={(v) => updateCreneau(index, "dateCreneau", v)}
                  />
                  <TextField
                    type="time"
                    size="small"
                    value={c.heureDebut}
                    onChange={(e) => updateCreneau(index, "heureDebut", e.target.value)}
                  />
                  <TextField
                    type="time"
                    size="small"
                    value={c.heureFin}
                    onChange={(e) => updateCreneau(index, "heureFin", e.target.value)}
                  />
                  <TextField
                    size="small"
                    placeholder="Lieu (optionnel)"
                    value={c.lieu}
                    onChange={(e) => updateCreneau(index, "lieu", e.target.value)}
                  />
                  <IconButton size="small" onClick={() => removeCreneau(index)} color="error">
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              ))}

              <Button variant="outlined" size="small" startIcon={<Plus size={16} />} onClick={addCreneau}>
                Ajouter un créneau
              </Button>
            </Box>
          )}

          {/* Options avancées */}
          <Accordion elevation={0} sx={{ border: "1px solid #e0e0e0", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Options avancées</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={choixMultiple} onChange={(e) => setChoixMultiple(e.target.checked)} />}
                  label="Permettre les réponses multiples"
                />
                <FormControlLabel
                  control={<Switch checked={anonyme} onChange={(e) => setAnonyme(e.target.checked)} />}
                  label="Réponses anonymes"
                />
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Rappels automatiques
                </Typography>
                <FormControlLabel
                  control={<Switch checked={rappelJ7Actif} onChange={(e) => setRappelJ7Actif(e.target.checked)} />}
                  label="Rappel J-7 avant la date limite"
                />
                <FormControlLabel
                  control={<Switch checked={rappelJ3Actif} onChange={(e) => setRappelJ3Actif(e.target.checked)} />}
                  label="Rappel J-3 avant la date limite"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        {nbDestinatairesEstime > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
            {nbDestinatairesEstime} destinataire{nbDestinatairesEstime > 1 ? "s" : ""} estimé{nbDestinatairesEstime > 1 ? "s" : ""}
          </Typography>
        )}
        <Button onClick={() => onClose()} disabled={saving}>
          Annuler
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSave(false)}
          disabled={saving || !hasSelection}
        >
          {saving ? "Enregistrement..." : "Sauvegarder brouillon"}
        </Button>
        {!isEdit && (
          <Button
            variant="contained"
            onClick={() => handleSave(true)}
            disabled={saving || !hasSelection}
            sx={{ bgcolor: "#4BB7A5", "&:hover": { bgcolor: "#3a9485" } }}
          >
            Envoyer immédiatement
          </Button>
        )}
      </DialogActions>

      {/* EmailComposer pour personnaliser l'email avant envoi */}
      {(() => {
        const defaultEmail = getDefaultSondageEmail();
        return (
          <EmailComposer
            open={composerOpen}
            onClose={() => setComposerOpen(false)}
            onConfirm={handleComposerConfirm}
            defaultSujet={defaultEmail.sujet}
            defaultCorps={defaultEmail.corps}
            contexte="sondage"
            nbDestinataires={nbDestinatairesEstime}
            allowScheduling={true}
            defaultImmediate={true}
          />
        );
      })()}
    </DialogMobile>
  );
}
